/**
 * SystemSettings 頁面
 * 系統設定管理
 */

import { useState, useEffect } from 'react';
import { Layout } from '../../components/Layout';
import { get, patch } from '../../api/superAdminClient';
import useSWR from 'swr';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorDisplay } from '../../components/ErrorDisplay';
import { Modal } from '../../components/Modal';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { zhTW } from 'date-fns/locale';

/**
 * 系統設定列表回應類型
 */
interface SystemSettingListResponse {
  success: boolean;
  data: Array<{
    id: string;
    key: string;
    value: Record<string, unknown>;
    description: string | null;
    updatedBy: string | null;
    createdAt: string;
    updatedAt: string;
  }>;
}

/**
 * 系統設定表單 Schema
 */
const systemSettingSchema = z.object({
  key: z.string().min(1, 'Key 為必填').max(255),
  value: z.record(z.string(), z.any()), // 後端要求 value 必須是 Record
  description: z.string().optional(),
});

type SystemSettingFormData = z.infer<typeof systemSettingSchema>;

/**
 * SystemSettings 頁面
 */
export function SystemSettings() {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingSetting, setEditingSetting] = useState<
    SystemSettingListResponse['data'][0] | null
  >(null);

  const { data, error, isLoading, mutate } = useSWR<SystemSettingListResponse>(
    '/api/super-admin/system-settings',
    get as any
  );

  const handleEdit = (setting: SystemSettingListResponse['data'][0]) => {
    setEditingSetting(setting);
    setIsCreateModalOpen(true);
  };

  return (
    <Layout title="系統設定">
      <div className="space-y-4">
        {/* 操作列 */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            共 {data?.data?.length || 0} 個系統設定
          </div>
          {/* 注意：後端目前沒有建立系統設定的 API，暫時隱藏建立按鈕 */}
          {/* <button
            onClick={handleCreate}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            + 新增系統設定
          </button> */}
        </div>

        {/* 系統設定列表 */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center">
              <LoadingSpinner size="lg" />
            </div>
          ) : error ? (
            <div className="p-8">
              <ErrorDisplay error={error} onRetry={() => mutate()} />
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Key
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    更新時間
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data?.data?.map(setting => {
                  const displayValue = JSON.stringify(setting.value, null, 2);

                  return (
                    <tr key={setting.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {setting.key}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <div className="max-w-md">
                          <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto">
                            {displayValue}
                          </pre>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {format(
                          new Date(setting.updatedAt),
                          'yyyy-MM-dd HH:mm',
                          { locale: zhTW }
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => handleEdit(setting)}
                          className="text-primary hover:text-primary/80 transition-colors"
                        >
                          編輯
                        </button>
                        {/* 注意：後端目前沒有刪除系統設定的 API，暫時隱藏刪除按鈕 */}
                        {/* <button
                          onClick={() => handleDelete(setting)}
                          className="text-red-600 hover:text-red-800 transition-colors"
                        >
                          刪除
                        </button> */}
                      </td>
                    </tr>
                  );
                }) || (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-6 py-4 text-center text-gray-500"
                    >
                      尚無系統設定
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* 編輯 Modal */}
        <SystemSettingFormModal
          isOpen={isCreateModalOpen}
          onClose={() => {
            setIsCreateModalOpen(false);
            setEditingSetting(null);
          }}
          setting={editingSetting}
          onSuccess={() => {
            mutate();
            setIsCreateModalOpen(false);
            setEditingSetting(null);
          }}
        />
      </div>
    </Layout>
  );
}

/**
 * 系統設定表單 Modal
 */
interface SystemSettingFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  setting: SystemSettingListResponse['data'][0] | null;
  onSuccess: () => void;
}

function SystemSettingFormModal({
  isOpen,
  onClose,
  setting,
  onSuccess,
}: SystemSettingFormModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [valueType, setValueType] = useState<
    'string' | 'number' | 'boolean' | 'json'
  >('string');
  const [valueInput, setValueInput] = useState<string>('');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SystemSettingFormData>({
    resolver: zodResolver(systemSettingSchema),
    defaultValues: setting
      ? {
          key: setting.key,
          value: setting.value,
          description: setting.description || '',
        }
      : {
          key: '',
          value: {},
          description: '',
        },
  });

  // 根據 value 類型判斷輸入元件類型
  // 注意：後端 value 是 Record，但我們可以從 value 的內容推斷顯示類型
  const getValueType = (
    value: Record<string, unknown>
  ): 'string' | 'number' | 'boolean' | 'json' => {
    // 如果 value 只有一個 key 且 key 是 'value'，可能是包裝的值
    if (Object.keys(value).length === 1 && 'value' in value) {
      const innerValue = value.value;
      if (typeof innerValue === 'boolean') return 'boolean';
      if (typeof innerValue === 'number') return 'number';
      if (typeof innerValue === 'string') return 'string';
    }
    // 否則當作 JSON 物件處理
    return 'json';
  };

  // 初始化 value type 和 input
  useEffect(() => {
    if (setting) {
      const type = getValueType(setting.value);
      setValueType(type);
      if (type === 'json') {
        setValueInput(JSON.stringify(setting.value, null, 2));
      } else if (type === 'boolean' && 'value' in setting.value) {
        setValueInput(String(setting.value.value));
      } else if (type === 'number' && 'value' in setting.value) {
        setValueInput(String(setting.value.value));
      } else if (type === 'string' && 'value' in setting.value) {
        setValueInput(String(setting.value.value));
      } else {
        setValueInput(JSON.stringify(setting.value, null, 2));
      }
    } else {
      setValueType('json');
      setValueInput('{}');
    }
  }, [setting]);

  const parseValue = (input: string, type: string): Record<string, unknown> => {
    // 後端要求 value 必須是 Record<string, any>
    if (type === 'json') {
      try {
        const parsed = JSON.parse(input);
        // 確保是物件
        if (
          typeof parsed === 'object' &&
          parsed !== null &&
          !Array.isArray(parsed)
        ) {
          return parsed;
        }
        // 如果不是物件，包裝成物件
        return { value: parsed };
      } catch {
        throw new Error('無效的 JSON 格式');
      }
    }
    // 其他類型包裝成物件
    if (type === 'boolean') {
      return { value: input === 'true' };
    }
    if (type === 'number') {
      return { value: parseFloat(input) };
    }
    return { value: input };
  };

  const onSubmit = async (formData: SystemSettingFormData) => {
    setIsSubmitting(true);
    try {
      const parsedValue = parseValue(valueInput, valueType);
      const payload = {
        ...formData,
        value: parsedValue,
      };

      // 注意：後端目前只有更新 API，沒有建立 API
      if (!setting) {
        toast.error('後端目前不支援建立系統設定');
        return;
      }

      // 更新
      await patch(`/api/super-admin/system-settings/${setting.key}`, payload);
      toast.success('系統設定更新成功');
      reset();
      setValueInput('');
      setValueType('string');
      onSuccess();
    } catch (error) {
      if (
        error instanceof SyntaxError ||
        (error instanceof Error && error.message.includes('JSON'))
      ) {
        toast.error('JSON 格式錯誤，請檢查輸入');
      } else {
        toast.error(error instanceof Error ? error.message : '操作失敗');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const footer = (
    <>
      <button
        onClick={onClose}
        disabled={isSubmitting}
        className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
      >
        取消
      </button>
      <button
        onClick={handleSubmit(onSubmit)}
        disabled={isSubmitting}
        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
      >
        {isSubmitting ? '處理中...' : '更新'}
      </button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="編輯系統設定"
      footer={footer}
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Key <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            {...register('key')}
            disabled={!!setting} // 編輯時不可修改 key
            className={`
              w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent
              ${setting ? 'bg-gray-100 cursor-not-allowed' : ''}
              ${errors.key ? 'border-red-500' : 'border-gray-300'}
            `}
            placeholder="例如: max_file_size"
          />
          {errors.key && (
            <p className="mt-1 text-sm text-red-600">{errors.key.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Value 類型
          </label>
          <select
            value={valueType}
            onChange={e => {
              setValueType(e.target.value as typeof valueType);
              if (e.target.value === 'boolean') {
                setValueInput('false');
              } else if (e.target.value === 'number') {
                setValueInput('0');
              } else if (e.target.value === 'json') {
                setValueInput('{}');
              } else {
                setValueInput('');
              }
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="string">字串</option>
            <option value="number">數字</option>
            <option value="boolean">布林值</option>
            <option value="json">JSON</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Value <span className="text-red-500">*</span>
          </label>
          {valueType === 'boolean' ? (
            <select
              value={valueInput}
              onChange={e => setValueInput(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="true">true</option>
              <option value="false">false</option>
            </select>
          ) : valueType === 'json' ? (
            <textarea
              value={valueInput}
              onChange={e => setValueInput(e.target.value)}
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm"
              placeholder='{"key": "value"}'
            />
          ) : (
            <input
              type={valueType === 'number' ? 'number' : 'text'}
              value={valueInput}
              onChange={e => setValueInput(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder={valueType === 'number' ? '0' : '輸入值'}
            />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            描述
          </label>
          <textarea
            {...register('description')}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="系統設定的描述說明"
          />
        </div>
      </form>
    </Modal>
  );
}
